import { useState } from "react";
import { Radar, Loader2, AlertTriangle, ShieldCheck, Info, ThermometerSun, Droplets, CloudRain, Sprout, FlaskConical, ExternalLink, ChevronDown, ChevronUp, Zap, X, Clock, TrendingUp, TrendingDown, Minus, Bookmark } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { authFetch } from "@/lib/api";
import { toast } from "sonner";
import { API_BASE } from "./constants";

// Convertit 0..1 en % arrondis
const pct = (s) => (s == null ? 0 : Math.round(s * 100));

const NIVEAU_STYLE = {
  "Élevé":   { bg: "#DC262615", color: "#DC2626", label: "Élevé",   ring: "#DC2626" },
  "Modéré":  { bg: "#EA580C15", color: "#EA580C", label: "Modéré",  ring: "#EA580C" },
  "Faible":  { bg: "#16A34A15", color: "#16A34A", label: "Faible",  ring: "#16A34A" },
  "Non calculable": { bg: "#F3F4F6", color: "#6B7280", label: "Non calculable", ring: "#9CA3AF" },
};

const PRIORITY_STYLE = {
  P0: { bg: "#7F1D1D", color: "#FFF" },
  P1: { bg: "#DC2626", color: "#FFF" },
  P2: { bg: "#F59E0B", color: "#111" },
  P3: { bg: "#65A30D", color: "#FFF" },
};

function ScoreDial({ score, niveau }) {
  const p = pct(score);
  const style = NIVEAU_STYLE[niveau] || NIVEAU_STYLE["Faible"];
  return (
    <div className="relative w-16 h-16 shrink-0">
      <svg viewBox="0 0 36 36" className="w-16 h-16 -rotate-90">
        <path d="M18 2 a 16 16 0 0 1 0 32 a 16 16 0 0 1 0 -32" stroke="#F3F4F6" strokeWidth="3" fill="none" />
        <path d="M18 2 a 16 16 0 0 1 0 32 a 16 16 0 0 1 0 -32" stroke={style.ring} strokeWidth="3" fill="none" strokeDasharray={`${p} 100`} strokeLinecap="round" />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="text-sm font-black" style={{ color: style.color }}>{p}</span>
        <span className="text-[8px] font-semibold -mt-0.5" style={{ color: style.color }}>/100</span>
      </div>
    </div>
  );
}

function BioagressorCard({ risk, expanded, onToggle }) {
  const style = NIVEAU_STYLE[risk.niveau] || NIVEAU_STYLE["Faible"];
  const pStyle = PRIORITY_STYLE[risk.priority] || {};
  return (
    <div className="rounded-lg border border-gray-200 bg-white overflow-hidden" data-testid={`vigie-card-${risk.bioagressor_id}`}>
      <button
        onClick={onToggle}
        className="w-full text-left p-3 flex items-start gap-3 hover:bg-gray-50 transition-colors"
        data-testid={`vigie-card-toggle-${risk.bioagressor_id}`}
      >
        <ScoreDial score={risk.score} niveau={risk.niveau} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 flex-wrap">
            <h3 className="text-sm font-bold text-gray-900 truncate">{risk.name_fr}</h3>
            {risk.priority && (
              <span className="text-[9px] px-1.5 py-0.5 rounded font-black" style={pStyle}>{risk.priority}</span>
            )}
            {risk.quality_grade && (
              <span className="text-[9px] px-1.5 py-0.5 rounded font-bold bg-slate-200 text-slate-700" title="Grade qualité des données (A=meilleure, D=fiche minimale)">
                {risk.quality_grade}
              </span>
            )}
            <Badge className="text-[9px] border-0 px-1.5 py-0" style={{ background: style.bg, color: style.color }}>
              {style.label}
            </Badge>
          </div>
          {risk.scientific_name && (
            <p className="text-[10px] italic text-gray-500 mt-0.5">{risk.scientific_name}</p>
          )}
          <div className="text-[10px] text-gray-600 mt-1 flex items-center gap-2 flex-wrap">
            <span>Confiance : <b>{risk.confiance}</b></span>
            <span>·</span>
            <span>{risk.nb_dimensions_quantifiees} facteur{risk.nb_dimensions_quantifiees > 1 ? "s" : ""} évalué{risk.nb_dimensions_quantifiees > 1 ? "s" : ""}</span>
            {risk.trend && (
              <>
                <span>·</span>
                <span className={`flex items-center gap-0.5 font-semibold ${risk.trend.direction === "hausse" ? "text-red-600" : risk.trend.direction === "baisse" ? "text-emerald-600" : "text-gray-500"}`}>
                  {risk.trend.direction === "hausse" ? <TrendingUp className="w-3 h-3" /> : risk.trend.direction === "baisse" ? <TrendingDown className="w-3 h-3" /> : <Minus className="w-3 h-3" />}
                  {risk.trend.direction} ({risk.trend.delta > 0 ? "+" : ""}{risk.trend.delta})
                </span>
              </>
            )}
            {risk.bsv_citations_count > 0 && (
              <>
                <span>·</span>
                <span className="text-emerald-700 flex items-center gap-0.5" title="Nombre de citations BSV documentées">
                  <Bookmark className="w-3 h-3" /> {risk.bsv_citations_count} BSV
                </span>
              </>
            )}
            {risk.recent_incidents_count > 0 && (
              <>
                <span>·</span>
                <span className="text-red-700 font-bold flex items-center gap-0.5">
                  <AlertTriangle className="w-3 h-3" /> {risk.recent_incidents_count} foyer(s) 30j
                </span>
              </>
            )}
          </div>
        </div>
        {expanded ? <ChevronUp className="w-4 h-4 text-gray-400 shrink-0 mt-1" /> : <ChevronDown className="w-4 h-4 text-gray-400 shrink-0 mt-1" />}
      </button>

      {expanded && (
        <div className="border-t bg-gray-50/60 p-3 space-y-2.5 text-[11.5px]">
          {risk.degree_days && (
            <div className="p-2 rounded bg-orange-50 border border-orange-200">
              <p className="text-[10px] uppercase font-semibold text-orange-800 mb-1">Degrés-jours (base {risk.degree_days.base_c}°C)</p>
              <div className="flex gap-3 text-[11px] text-orange-900">
                <span>7j : <b>{risk.degree_days.dd_7d}</b></span>
                <span>14j : <b>{risk.degree_days.dd_14d}</b></span>
                <span>30j : <b>{risk.degree_days.dd_30d}</b></span>
              </div>
              {risk.degree_days.thresholds?.length > 0 && (
                <p className="text-[9px] text-orange-700 mt-0.5">Seuils documentés : {risk.degree_days.thresholds.join(" · ")} DJ</p>
              )}
            </div>
          )}
          {risk.facteurs_declencheurs?.length > 0 && (
            <div>
              <p className="text-[10px] uppercase font-semibold text-gray-600 mb-1">Facteurs déclencheurs (météo + sol)</p>
              <ul className="space-y-1">
                {risk.facteurs_declencheurs.map((f, i) => (
                  <li key={i} className="flex items-start gap-1.5 text-gray-700">
                    <span className="text-emerald-600 mt-0.5">›</span>
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {risk.preventive_actions_non_chemical?.length > 0 && (
            <div>
              <p className="text-[10px] uppercase font-semibold text-emerald-700 mb-1 flex items-center gap-1">
                <Sprout className="w-3 h-3" /> Actions préventives non chimiques recommandées
              </p>
              <ul className="space-y-0.5">
                {risk.preventive_actions_non_chemical.map((a, i) => (
                  <li key={i} className="text-gray-800 pl-4 relative">
                    <span className="absolute left-0 text-emerald-600">•</span>{a}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {risk.chemical_warning && (
            <div className="p-2 rounded bg-amber-50 border border-amber-300 text-amber-900 flex items-start gap-1.5">
              <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
              <p className="text-[10.5px]"><b>Rappel réglementaire :</b> {risk.chemical_warning}</p>
            </div>
          )}
          {risk.sources?.length > 0 && (
            <details className="text-[10px] text-gray-500">
              <summary className="cursor-pointer hover:text-gray-800">Sources ({risk.sources.length})</summary>
              <ul className="mt-1 space-y-0.5 pl-3">
                {risk.sources.map((s, i) => (
                  <li key={i}>
                    {s.url ? (
                      <a href={s.url} target="_blank" rel="noopener noreferrer" className="text-emerald-700 hover:underline inline-flex items-center gap-1">
                        {s.titre || s.url} <ExternalLink className="w-2.5 h-2.5" />
                      </a>
                    ) : (
                      <span>{s.titre || JSON.stringify(s)}</span>
                    )}
                  </li>
                ))}
              </ul>
            </details>
          )}
        </div>
      )}
    </div>
  );
}

export default function VigieSanitaireTab({ parcelId, cultureType, embedded = false, onClose }) {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [expandedId, setExpandedId] = useState(null);

  const launch = async () => {
    setLoading(true);
    setResult(null);
    try {
      const res = await authFetch(`${API_BASE}/sanitary/vigie/${parcelId}`);
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        toast.error(err?.detail || "Erreur lors du lancement de la Vigie Sanitaire");
        setLoading(false);
        return;
      }
      const data = await res.json();
      setResult(data);
      if (data.top_risques?.[0]) setExpandedId(data.top_risques[0].bioagressor_id);
      const nbEleves = (data.top_risques || []).filter(r => r.niveau === "Élevé").length;
      if (nbEleves > 0) {
        toast.warning(`${nbEleves} bioagresseur${nbEleves > 1 ? "s" : ""} à risque Élevé détecté${nbEleves > 1 ? "s" : ""}`);
      } else {
        toast.success("Vigie Sanitaire terminée");
      }
    } catch (e) {
      toast.error("Erreur réseau");
    }
    setLoading(false);
  };

  const content = (
    <div className="p-3 sm:p-4 space-y-4 overflow-y-auto" data-testid="vigie-sanitaire-tab" style={{ WebkitOverflowScrolling: "touch" }}>
      {/* Header intro */}
      <div className="rounded-xl p-4 border border-emerald-200" style={{ background: "linear-gradient(135deg, #ECFDF5, #F0FDF4)" }}>
        <div className="flex items-start gap-3">
          <div className="w-11 h-11 rounded-full bg-emerald-700 flex items-center justify-center shrink-0 shadow-md">
            <Radar className="w-5 h-5 text-white" />
          </div>
          <div className="flex-1">
            <h2 className="text-base font-black text-emerald-900">Vigie Sanitaire</h2>
            <p className="text-[11.5px] text-emerald-800 leading-relaxed mt-0.5">
              Analyse instantanée du risque bioagresseurs pour <b>{cultureType || "cette culture"}</b>.
              Croise la <b>météo actuelle</b>, votre <b>dernière analyse de sol</b> et vos <b>dernières pratiques</b> avec 63 fiches
              bioagresseurs Réunion/Mayotte enrichies BSV.
            </p>
          </div>
        </div>
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <Button
            size="sm"
            className="h-9 text-xs bg-emerald-700 hover:bg-emerald-800 text-white font-semibold shadow-md"
            onClick={launch}
            disabled={loading}
            data-testid="launch-vigie-btn"
          >
            {loading ? <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> Analyse en cours…</> : <><Zap className="w-3.5 h-3.5 mr-1.5" /> Lancer la Vigie Sanitaire</>}
          </Button>
          {result?.base_version && (
            <span className="text-[10px] text-emerald-800 opacity-70">Base : v{result.base_version.slice(-8)}</span>
          )}
        </div>
      </div>

      {/* Résultats */}
      {loading && (
        <div className="text-center py-8 text-gray-500 text-xs">
          <Loader2 className="w-8 h-8 mx-auto mb-2 animate-spin text-emerald-600" />
          Croisement météo + sol + fiches bioagresseurs…
        </div>
      )}

      {result && !loading && (
        <>
          {/* Alerte pertinence des données (fraîcheur) */}
          {result.data_quality_report?.warnings?.length > 0 && (
            <div className="rounded-lg border border-amber-300 bg-amber-50 p-3" data-testid="vigie-data-quality-warnings">
              <p className="text-[11px] font-bold text-amber-900 mb-1 flex items-center gap-1">
                <AlertTriangle className="w-3.5 h-3.5" /> Données ignorées (trop anciennes)
              </p>
              <ul className="text-[10.5px] text-amber-800 space-y-0.5">
                {result.data_quality_report.warnings.map((w, i) => (
                  <li key={i} className="pl-3 relative"><span className="absolute left-0">•</span>{w}</li>
                ))}
              </ul>
            </div>
          )}

          {/* Contexte temporel (mémoire IA 24h → 30j) */}
          {result.temporal_context?.windows && (
            <div className="rounded-lg border border-emerald-200 bg-emerald-50/50 p-3" data-testid="vigie-temporal-context">
              <p className="text-[10px] uppercase font-semibold text-emerald-800 mb-2 flex items-center gap-1">
                <Clock className="w-3 h-3" /> Mémoire temporelle · fenêtres météo
              </p>
              <div className="grid grid-cols-5 gap-1.5 text-[10px]">
                {["24h", "72h", "7d", "14d", "30d"].map((w) => {
                  const wd = result.temporal_context.windows[w] || {};
                  return (
                    <div key={w} className="p-1.5 bg-white rounded border border-emerald-100 text-center">
                      <p className="text-[9px] font-black text-emerald-800 uppercase">{w === "7d" ? "7j" : w === "14d" ? "14j" : w === "30d" ? "30j" : w}</p>
                      <p className="text-[10.5px] font-bold text-gray-800">{wd.rain_mm ?? "—"} mm</p>
                      {wd.hours_high_humidity !== undefined && (
                        <p className="text-[9px] text-cyan-700">{wd.hours_high_humidity ?? "—"}h HR≥90</p>
                      )}
                      {wd.tmean_c !== undefined && (
                        <p className="text-[9px] text-orange-700">{wd.tmin_c}–{wd.tmax_c}°C</p>
                      )}
                    </div>
                  );
                })}
              </div>
              {result.temporal_context.recent_incidents_30d_total > 0 && (
                <p className="mt-1.5 text-[10px] text-red-700 flex items-center gap-1">
                  <AlertTriangle className="w-3 h-3" />
                  <b>{result.temporal_context.recent_incidents_30d_total}</b> foyer{result.temporal_context.recent_incidents_30d_total > 1 ? "s" : ""} déclaré{result.temporal_context.recent_incidents_30d_total > 1 ? "s" : ""} sur 30 j
                </p>
              )}
              {result.temporal_context.previous_vigie_at && (
                <p className="mt-1 text-[9px] text-emerald-700 opacity-70">
                  Vigie précédente : {new Date(result.temporal_context.previous_vigie_at).toLocaleString("fr-FR")}
                </p>
              )}
            </div>
          )}

          {/* Bloc inputs (transparence) */}
          <div className="rounded-lg border border-gray-200 bg-white p-3">
            <p className="text-[10px] uppercase font-semibold text-gray-600 mb-2">Données prises en compte à l'instant T</p>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2 text-[11px]">
              <div className="p-2 bg-blue-50 rounded flex items-start gap-1.5">
                <ThermometerSun className="w-3.5 h-3.5 text-blue-700 mt-0.5" />
                <div>
                  <p className="text-blue-900 font-semibold">{result.inputs?.weather?.temperature_c ?? "—"}°C</p>
                  <p className="text-blue-700 text-[9px]">Air · HR {result.inputs?.weather?.humidity_percent ?? "—"}%</p>
                </div>
              </div>
              <div className="p-2 bg-sky-50 rounded flex items-start gap-1.5">
                <CloudRain className="w-3.5 h-3.5 text-sky-700 mt-0.5" />
                <div>
                  <p className="text-sky-900 font-semibold">{result.inputs?.weather?.rain_24h_mm ?? "—"} mm / 24h</p>
                  <p className="text-sky-700 text-[9px]">{result.inputs?.weather?.rain_72h_mm ?? "—"} mm / 72h</p>
                </div>
              </div>
              <div className="p-2 bg-cyan-50 rounded flex items-start gap-1.5" title="Nb d'heures des 24 dernières heures avec humidité relative ≥ 90 %. Approximation agrométéo (proxy) — pas une mesure directe du film d'eau sur les feuilles.">
                <Droplets className="w-3.5 h-3.5 text-cyan-700 mt-0.5" />
                <div>
                  <p className="text-cyan-900 font-semibold">{result.inputs?.weather?.leaf_wetness_h ?? "—"} h</p>
                  <p className="text-cyan-700 text-[9px]">humectation (HR≥90 %) <Info className="w-2.5 h-2.5 inline" /></p>
                </div>
              </div>
              <div className="p-2 bg-orange-50 rounded flex items-start gap-1.5" title="Données satellite ERA5-Land (Copernicus)">
                <ThermometerSun className="w-3.5 h-3.5 text-orange-700 mt-0.5" />
                <div>
                  <p className="text-orange-900 font-semibold">{result.inputs?.weather?.soil_temperature_c ?? "—"}°C</p>
                  <p className="text-orange-700 text-[9px]">Sol (satellite)</p>
                </div>
              </div>
              <div className="p-2 bg-amber-50 rounded flex items-start gap-1.5" title={result.inputs?.soil?.source_pH || ""}>
                <FlaskConical className="w-3.5 h-3.5 text-amber-700 mt-0.5" />
                <div>
                  <p className={`font-semibold ${result.inputs?.soil?.ph_measured_stale ? "text-gray-400 line-through" : "text-amber-900"}`}>
                    pH {result.inputs?.soil?.ph_measured_stale ? "trop ancien" : (result.inputs?.soil?.ph_measured ?? "—")}
                  </p>
                  <p className="text-amber-700 text-[9px]">
                    {result.inputs?.soil?.ph_measured_stale
                      ? `ignoré (${result.inputs?.soil?.ph_measured_age_days}j)`
                      : (result.ph_estimate?.confiance || "non renseigné")}
                    {result.ph_estimate?.tendance && !result.inputs?.soil?.ph_measured_stale ? ` · ${result.ph_estimate.tendance}` : ""}
                  </p>
                </div>
              </div>
            </div>
            {/* Bandeau registre parcellaire */}
            {result.inputs?.holding && (
              <div className="mt-2 text-[10.5px] text-gray-600 flex items-center gap-2 flex-wrap border-t pt-2">
                <span className="font-semibold text-gray-700">Registre :</span>
                {result.inputs.holding.registre_rempli ? (
                  <>
                    <span>SIRET <b>{result.inputs.holding.siret_holder}</b></span>
                    {result.inputs.holding.business_name && <span>· {result.inputs.holding.business_name}</span>}
                  </>
                ) : (
                  <span className="italic text-gray-500">non rempli (Vigie active malgré tout)</span>
                )}
                {result.inputs.holding.rpg_id && <span>· RPG {result.inputs.holding.rpg_id}</span>}
                {result.inputs.holding.commune && <span>· {result.inputs.holding.commune}</span>}
                {result.inputs.holding.surface_ha && <span>· {result.inputs.holding.surface_ha.toFixed?.(2) ?? result.inputs.holding.surface_ha} ha</span>}
              </div>
            )}
            {result.inputs?.soil?.soil_moisture_percent_satellite != null && (
              <p className="mt-1 text-[10px] text-gray-500 flex items-center gap-1">
                <Droplets className="w-2.5 h-2.5" />
                Humidité sol satellite : {result.inputs.soil.soil_moisture_percent_satellite}% ·
                <span className="opacity-70">{result.inputs?.soil?.source_satellite}</span>
              </p>
            )}
            {result.inputs?.soil?.details && (
              <div className="mt-1 text-[10px] text-gray-500 flex items-center gap-2 flex-wrap">
                <span className="font-semibold">Analyse sol :</span>
                {result.inputs.soil.details.laboratoire && <span>{result.inputs.soil.details.laboratoire}</span>}
                {result.inputs.soil.details.matiere_organique != null && <span>· MO {result.inputs.soil.details.matiere_organique}%</span>}
                {result.inputs.soil.details.cec != null && <span>· CEC {result.inputs.soil.details.cec}</span>}
              </div>
            )}
            {result.inputs?.recent_practice && (
              <p className="mt-1 text-[10.5px] text-gray-600 flex items-center gap-1">
                <Info className="w-3 h-3" />
                Dernière pratique : <b>{result.inputs.recent_practice.product}</b>
                {result.inputs.recent_practice.date ? ` (${result.inputs.recent_practice.date})` : ""}
              </p>
            )}
          </div>

          {/* Compteurs */}
          <div className="flex items-center gap-3 text-[11px] text-gray-600">
            <span><b className="text-gray-900">{result.top_risques?.length || 0}</b> bioagresseur{(result.top_risques?.length || 0) > 1 ? "s" : ""} scorés</span>
            {result.nb_bioagresseurs_candidats > 0 && (
              <>
                <span className="opacity-40">·</span>
                <span>{result.nb_bioagresseurs_candidats} candidats sur la culture</span>
              </>
            )}
            {result.non_calculables_count > 0 && (
              <>
                <span className="opacity-40">·</span>
                <span className="text-gray-500">{result.non_calculables_count} non calculable{result.non_calculables_count > 1 ? "s" : ""}</span>
              </>
            )}
          </div>

          {/* Liste des risques */}
          {result.top_risques?.length > 0 ? (
            <div className="space-y-2" data-testid="vigie-results-list">
              {result.top_risques.map((r) => (
                <BioagressorCard
                  key={r.bioagressor_id}
                  risk={r}
                  expanded={expandedId === r.bioagressor_id}
                  onToggle={() => setExpandedId(expandedId === r.bioagressor_id ? null : r.bioagressor_id)}
                />
              ))}
            </div>
          ) : (
            <div className="rounded-lg border border-gray-200 bg-white p-6 text-center">
              <ShieldCheck className="w-10 h-10 mx-auto text-emerald-500 mb-2" />
              <p className="text-sm font-semibold text-gray-800">Aucun risque calculable à l'instant T</p>
              <p className="text-[11px] text-gray-500 mt-1">
                Aucun bioagresseur ne présente de seuil documenté croisant les conditions actuelles.
                {result.nb_bioagresseurs_candidats === 0 && ` La culture "${result.culture}" n'est peut-être pas encore couverte par la base.`}
              </p>
            </div>
          )}

          {/* Safety notice */}
          <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 text-amber-700 shrink-0 mt-0.5" />
            <p className="text-[11px] text-amber-900 leading-relaxed">{result.safety_notice}</p>
          </div>
        </>
      )}

      {!result && !loading && (
        <div className="rounded-lg border-2 border-dashed border-gray-300 bg-white p-6 text-center text-gray-500 text-xs" data-testid="vigie-empty">
          <Radar className="w-10 h-10 mx-auto opacity-30 mb-2" />
          <p>Cliquez sur <b>Lancer la Vigie Sanitaire</b> pour obtenir un scoring bioagresseurs à l'instant T.</p>
          <p className="mt-1.5 text-[10px] opacity-70">Zéro produit chimique recommandé — uniquement des actions préventives et un rappel réglementaire par espèce.</p>
        </div>
      )}
    </div>
  );

  if (embedded) {
    return content;
  }

  // Modal mode : bandeau + zone scrollable + close btn
  return (
    <div className="fixed inset-0 z-[3000] bg-black/60 flex items-center justify-center p-2 sm:p-4" data-testid="vigie-sanitaire-modal">
      <div className="bg-white rounded-xl w-full max-w-3xl h-[95vh] overflow-hidden flex flex-col shadow-2xl">
        <div className="flex items-center justify-between px-4 py-3 border-b bg-gradient-to-r from-emerald-800 to-emerald-700 text-white shrink-0">
          <div>
            <div className="text-[11px] uppercase tracking-widest opacity-90">Vigie Sanitaire · {cultureType || "—"}</div>
            <div className="text-base font-bold flex items-center gap-2">
              <Radar className="w-4 h-4" /> Alerte bioagresseurs à l'instant T
            </div>
          </div>
          <button
            onClick={onClose}
            className="h-8 w-8 rounded-full hover:bg-white/20 flex items-center justify-center"
            data-testid="close-vigie-modal"
            aria-label="Fermer"
          >
            <X className="w-4 h-4 text-white" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto">{content}</div>
      </div>
    </div>
  );
}
